<script setup lang="ts">
import { avtalegiroSchema } from '@/components/Invoice/InvoicePaymentOptionSchema'
import CheckoutLayout from '@/components/Layout/CheckoutLayout.vue'
import { usePejer } from '@/Pejer'
import { toTypedSchema } from '@vee-validate/zod'
import { CircleCheckBig } from 'lucide-vue-next'
import DataStateButton from 'pejer/src/components/DataState/DataStateButton.vue'
import DataStateSkeleton from 'pejer/src/components/DataState/DataStateSkeleton.vue'
import AddressField from 'pejer/src/components/Form/AddressField.vue'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'pejer/src/components/ui/form'
import { Input } from 'pejer/src/components/ui/input'
import { Separator } from 'pejer/src/components/ui/separator'
import usePejerMutation from 'pejer/src/usePejerMutation'
import usePejerQuery from 'pejer/src/usePejerQuery'
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import z from 'zod'

const pejer = usePejer()
const {
  mutateAsync,
  dataState,
  data: mutationData,
} = usePejerMutation(
  pejer.urls.avtaleGiro.createAvtaleGiroAgreement,
  {
    queryKeys: [['avtalegiro-agreement']],
    // onSuccess: (data: InvoiceCreationResponse) => {
    //   emits('onInvoicePaid', data)
    // },
  },
  {
    ignoreAuthorization: true,
  }
)

const route = useRoute()

const { data, dataState: getDataState } = usePejerQuery(
  pejer.urls.avtaleGiro.getAvtaleGiro,
  {
    queryKey: ['avtalegiro-agreement'],
    pathArgs: {
      avtaleGiroOnboardingLinkID: route.params.avtaleGiroOnboardingLinkID as string,
    },
  },
  {
    ignoreAuthorization: true,
  }
)

const kidField = ref<HTMLInputElement | null>(null)
const ssnField = ref<HTMLInputElement | null>(null)
const accountField = ref<HTMLInputElement | null>(null)
const returnUrlField = ref<HTMLInputElement | null>(null)

const onSubmit = async (values: z.infer<typeof avtalegiroSchema>) => {
  console.log('values', values)
  const formattedValues = {
    ...values,
  }
  const res = await mutateAsync({
    pathArgs: {
      avtaleGiroOnboardingLinkID: route.params.avtaleGiroOnboardingLinkID as string,
    },
    body: formattedValues,
  })

  if (!res.shouldRedirectToAvtaleGiroSetup) {
    window.location.href = res.redirectBackUrl
    return
  }

  kidField.value!.value = res.kidReference
  ssnField.value!.value = values.customerSSN!
  accountField.value!.value = res.accountNumber
  returnUrlField.value!.value = res.redirectBackUrl

  const submitFormButton = document.getElementById('submitFormButton') as HTMLInputElement
  submitFormButton.click()
}

const submitButton = ref<HTMLButtonElement | null>(null)
const formSchema = toTypedSchema(avtalegiroSchema)
</script>
<template>
  <CheckoutLayout>
    <template #left>
      <div
        class="flex flex-col items-center w-full justify-center text-slate-700 pb-4 max-w-[450px] place-self-end"
        v-if="!data || !data.hasActiveAgreement"
      >
        <p class="text-xs text-slate-700">&nbsp;</p>
        <h1 class="text-xl font-bold">Aktiver AvtaleGiro</h1>
        <Form
          class="py-4 flex flex-col gap-4"
          :validation-schema="formSchema"
          keep-values
          @submit="onSubmit"
          v-slot="{ setFieldValue, values, errors }"
        >
          <Separator class="" />
          <div class="flex flex-col gap-4">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <FormField v-slot="{ componentField }" name="customerName">
                <FormItem>
                  <FormLabel class="text-secondaryBlack">Navn</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="Ola Nordmann" v-bind="componentField" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>

              <FormField v-slot="{ componentField }" name="customerSSN">
                <FormItem>
                  <FormLabel class="text-secondaryBlack">Fødselsnummer</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="12345678910" v-bind="componentField" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>
            </div>

            <FormField v-slot="{ componentField }" name="customerEmail">
              <FormItem>
                <FormLabel class="text-secondaryBlack">E-post</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="Ola.Nordmann@example.no"
                    v-bind="componentField"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>

            <AddressField
              addressName="customerAddress"
              postalCodeName="customerPostalCode"
              cityName="customerCity"
              ignore-validation-on-mount
            />
          </div>
          <DataStateButton
            :data-state="dataState"
            :variant="
              Object.values(values).some((x) => !x) || Object.values(errors).some(Boolean)
                ? 'outline'
                : 'default'
            "
            :disabled="Object.values(values).some((x) => !x) || Object.values(errors).some(Boolean)"
          >
            Fortsett
          </DataStateButton>
        </Form>

        <div class="hidden">
          <form action="https://pvu.avtalegiro.no/ecsa/start" method="post">
            <input type="hidden" name="companyName" id="companyName" value="BUCKY PAYMENTS AS" />
            <input
              type="hidden"
              name="companyAccountNo"
              id="companyAccountNo"
              :value="accountField?.value"
              ref="accountField"
            />
            <input type="hidden" name="kid" id="kid" :value="kidField?.value" ref="kidField" />
            <!-- <input type="hidden" name="amountLimit" id="amountLimit" value="5000" /> -->
            <input
              type="hidden"
              name="returnUrl"
              id="returnUrl"
              :value="mutationData?.redirectBackUrl"
              ref="returnUrlField"
            />
            <input
              type="hidden"
              name="notificationDisabled"
              id="notificationDisabled"
              value="true"
            />
            <input type="hidden" name="SSN" id="SSN" :value="ssnField?.value" ref="ssnField" />
            <input type="submit" id="submitFormButton" value="submit" ref="submitButton" />
          </form>
        </div>
      </div>
      <div v-else class="h-full flex justify-end items-center p-4">
        <div
          class="h-fit flex flex-col w-fit py-4 px-4 sm:py-10 sm:px-12 bg-white border-slate-200 border rounded-md shadow-merino-200"
        >
          <CircleCheckBig class="w-20 h-20 text-green-500 self-center mb-4" stroke-width="1.5" />
          <h1 class="text-xl font-semibold w-full text-center">AvtaleGiro-avtale inngått!</h1>
          <!-- <p class="text-sm text-slate-500">
            Du har inngått Avtale
          </p> -->
        </div>
      </div>
    </template>
    <template #right>
      <div class="flex flex-col gap-2 h-full justify-center">
        <div class="flex flex-col gap-2">
          <h1 class="flex flex-col">
            <div class="text-merino-950/60 text-xs">Gjelder for</div>
            <DataStateSkeleton :data-state="getDataState" sizing-class="w-1/2 h-8 bg-merino-200">
              <div class="text-merino-950 text-xl font-medium">
                {{ data?.businessName || '...' }}
              </div>
            </DataStateSkeleton>
          </h1>
          <h1 class="flex flex-col">
            <div class="text-merino-950/60 text-xs">Organisasjonsnummer</div>
            <DataStateSkeleton :data-state="getDataState" sizing-class="w-1/2 h-8 bg-merino-200">
              <div class="text-merino-950 text-xl font-medium">
                {{ data?.organizationID || '...' }}
              </div>
            </DataStateSkeleton>
          </h1>
        </div>
        <p class="text-sm text-merino-950/60" v-if="data && !data.isDirectPaymentAccount">
          <strong>AvtaleGiro</strong> utsendes av <strong>Bucky Payments AS</strong> på vegne av
          virksomheten.
        </p>
      </div>
    </template>
  </CheckoutLayout>
</template>
<style scoped>
input::placeholder {
  @apply text-gray-200;
}
</style>
